/**
 * CollectionCard Component
 * 
 * Card component for displaying a collection preview.
 */

import React from 'react';
import styled from '@emotion/styled';
import { Link } from 'react-router-dom';
import { CollectionEntity } from '../../../domain/entities/collection.entity';
import { useIntl } from '@/lib/intl';

const Card = styled.article`
  position: relative;
  border-radius: 16px;
  overflow: hidden;
  background: white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s, box-shadow 0.3s;
  aspect-ratio: 4/3;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  }
`;

const ImageContainer = styled.div`
  position: absolute;
  inset: 0;
`;

const CollectionImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const PlaceholderBackground = styled.div<{ index: number }>`
  width: 100%;
  height: 100%;
  background: ${({ index }) => {
    const gradients = [
      'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
      'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
      'linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)',
    ];
    return gradients[index % gradients.length];
  }};
`;

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.7) 0%, rgba(0, 0, 0, 0.2) 50%, transparent 100%);
`;

const Content = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 24px;
  color: white;
`;

const Title = styled.h3`
  font-size: 24px;
  font-weight: 700;
  margin: 0 0 4px 0;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
`;

const Description = styled.p`
  font-size: 14px;
  margin: 0 0 8px 0;
  opacity: 0.9;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const ProductCount = styled.span`
  font-size: 13px;
  opacity: 0.8;
`;

const TypeBadge = styled.span<{ type: 'manual' | 'smart' }>`
  position: absolute;
  top: 16px;
  right: 16px;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  background: ${({ type }) => (type === 'smart' ? 'rgba(59, 130, 246, 0.9)' : 'rgba(255, 255, 255, 0.9)')};
  color: ${({ type }) => (type === 'smart' ? 'white' : '#374151')};
`;

interface CollectionCardProps {
  collection: CollectionEntity;
  index?: number;
}

export function CollectionCard({ collection, index = 0 }: CollectionCardProps) {
  const { localizedPath } = useIntl();

  return (
    <Link to={localizedPath(`/collections/${collection.slug}`)} style={{ textDecoration: 'none' }}>
      <Card>
        <ImageContainer>
          {collection.imageUri ? (
            <CollectionImage src={collection.imageUri} alt={collection.title} loading="lazy" />
          ) : (
            <PlaceholderBackground index={index} />
          )}
        </ImageContainer>
        <Overlay />
        <TypeBadge type={collection.type}>
          {collection.type === 'smart' ? 'Smart' : 'Curated'}
        </TypeBadge>
        <Content>
          <Title>{collection.title}</Title>
          {collection.description && (
            <Description>{collection.description}</Description>
          )}
          <ProductCount>
            {collection.productCount} {collection.productCount === 1 ? 'product' : 'products'}
          </ProductCount>
        </Content>
      </Card>
    </Link>
  );
}

export default CollectionCard;

